import { IPropertyType } from "@/constants/PropertyType";
import { PrefectureInfo } from "@/utils/prefecture";
import { ShopInfo } from "./shop";
import { ReferralSource } from "./referralSource";

export enum Feature {
  TEL_OPTIONAL = "TEL_OPTIONAL",
  CONTACT_PREFERENCE_TEL = "CONTACT_PREFERENCE_TEL",
  CONTACT_PREFERENCE_MAIL = "CONTACT_PREFERENCE_MAIL",
  VALUATION_APPLY = "VALUATION_APPLY",
  VALUATION_AREA = "VALUATION_AREA",
  DIRECT_RESULT = "DIRECT_RESULT",
  HIDE_COMPONENTS = "HIDE_COMPONENTS",
  COLOR_THEME = "COLOR_THEME",
  ADVERTISEMENT_BANNER_OVERRIDE = "ADVERTISEMENT_BANNER_OVERRIDE",
  TITLE = "TITLE",
  ESTIMATION_RESULT_ANNOTATIONS = "ESTIMATION_RESULT_ANNOTATIONS",
  SERVICE_ANNOTATIONS = "SERVICE_ANNOTATIONS",
  CONFIRM_DIALOG_ANNOTATION = "CONFIRM_DIALOG_ANNOTATION",
  ESTIMATION_RESULT_CONTACT = "ESTIMATION_RESULT_CONTACT",
  GOOGLE_TAG_MANAGER = "GOOGLE_TAG_MANAGER",
  REASON_FOR_SALE = "REASON_FOR_SALE",
  NEW_DM_WEB_DESIGN = "NEW_DM_WEB_DESIGN",
  TRAFFIC_INFO_AUTO_LINK = "TRAFFIC_INFO_AUTO_LINK",
  CONNECTING_ROAD_INFO_AUTO = "CONNECTING_ROAD_INFO_AUTO",
  PRIVACY_POLICY_LABEL_TEXT = "PRIVACY_POLICY_LABEL_TEXT",
  SHOP_SETTINGS = "SHOP_SETTINGS",
  SITE_DOMAIN_NAME = "SITE_DOMAIN_NAME",
  PRICE_ADJUSTMENT = "PRICE_ADJUSTMENT",
  REFERRAL_SOURCE = "REFERRAL_SOURCE",
  TOTAL_STORY = "TOTAL_STORY",
  STRUCTURE = "STRUCTURE",
}

export enum ValuationAreaType {
  NONE = "0",
  ALL = "1",
  SOME = "2",
}

export const VALUATION_AREA_CONTROL = {
  PREF_ONLY: "pref-only",
  FULL: "full",
} as const;
export type ValuationAreaExtras = typeof VALUATION_AREA_CONTROL[keyof typeof VALUATION_AREA_CONTROL];

export const HIDDEN_COMPONENT_OPTIONS = {
  CUSTOMER_ADDRESS: "customer_address",
  PURCHASE_INTENTION: "purchase_intension",
  CUSTOMER_TEL: "customer_tel",
  CUSTOMER_KANA: "customer_kana",
  CUSTOMER_POST_CODE: "customer_post_code",
  CONTACT_TIME: "contact_time",
  TOTAL_STORY: "total_story",
  STRUCTURE: "structure",
} as const;
export type HiddenComponentSymbol = typeof HIDDEN_COMPONENT_OPTIONS[keyof typeof HIDDEN_COMPONENT_OPTIONS];

export const CONTACT_PREFERENCE_POSITION = {
  CUSTOMER_BLOCK: "customer_block",
  VALUATION_CONFIRM_MODAL: "valuation_confirm_modal",
};
export type ContactPreferencePosition = typeof CONTACT_PREFERENCE_POSITION[keyof typeof CONTACT_PREFERENCE_POSITION];

export type ContactPreferenceExtras = {
  default?: boolean;
  sentence?: {
    [key: string]: string;
    negative: string;
    positive: string;
    use: "negative" | "positive";
  };
  position?: ContactPreferencePosition;
  description?: string;
};

export type HideComponentsExtras = {
  hidden_components?: HiddenComponentSymbol[];
};

export type ValuationAreaOverrideExtras = {
  control?: ValuationAreaExtras;
};

export type ColorThemeExtras = {
  title?: {
    text?: string;
    background?: string;
  };
  required_icon?: {
    text?: string;
    background?: string;
    border?: string;
    borderRadius?: string;
  };
  invalid_state?: {
    background?: string;
    boarderRadius?: string;
    borderColor?: string;
  };
  mail_checkbox?: CheckboxColorExtras;
  primary?: string;
  secondary?: string;
  validation?: string;
};
export type ColorThemeOverrideExtras = {
  color?: ColorThemeExtras;
};

export type CheckboxColorExtras = {
  text?: string;
  background?: string;
  checkbox?: {
    checked: string;
    unchecked: string;
  };
  border?: string;
};

export const ADVERTISEMENT_BANNER_LAYOUT = {
  ROW: "row",
  COLUMN_2: "2_column",
};
export type AdvertisementBannerLayoutType = typeof ADVERTISEMENT_BANNER_LAYOUT[keyof typeof ADVERTISEMENT_BANNER_LAYOUT];

export const ADVERTISEMENT_BANNER_DATA_TYPE = {
  SMTRC: "smtrc",
};
export type AdvertisementBannerDataType = typeof ADVERTISEMENT_BANNER_DATA_TYPE[keyof typeof ADVERTISEMENT_BANNER_DATA_TYPE];
export type AdvertisementBanner = {
  layout?: AdvertisementBannerLayoutType;
  image: string;
  link: string;
  link_ext?: string;
  action?: "get" | "post" | "smtrc";
  data_type?: AdvertisementBannerDataType;
  caption?: string;
  description?: string;
};
export type AdvertisementBannerOverrideExtras = {
  banners?: AdvertisementBanner[];
};

export type TitleExtras = {
  text: string;
  background: string;
  color: string;
  replacementHeadingText1: string;
  replacementHeadingText2: string;
};

export type TitleOverrideExtras = {
  title?: TitleExtras;
};

export type EstimationResultAnnotationExtras = {
  annotations?: string[];
};

export type ServiceAnnotationExtras = {
  annotations?: string[];
};

export type ConfirmDialogAnnotationExtras = {
  annotation?: string;
};

export type EstimationResultContactExtras = {
  lead?: string;
  tel_additional_text?: string;
  contact_link_text?: string;
  business_hour?: string;
  regular_holiday?: string;
};

export type EstimationResultContactOverrideExtras = {
  contact?: EstimationResultContactExtras;
};

export type GoogleTagManagerExtras = {
  googleTagManagerId?: string;
};

export type ReasonForSaleExtras = {
  enabled?: boolean;
};

export type TrafficInfoAutoLinkExtras = {
  enabled?: boolean;
};

export type ConnectingRoadInfoAutoExtras = {
  enabled?: boolean;
};

export type PrivacyPolicyLabelTextExtras = {
  text?: string;
};

export type NewThemeEnabled = {
  enabled?: boolean;
  color_theme?: ColorThemeExtras;
  title?: {
    background?: string;
    color?: string;
  };
};

export type ShopSettingsExtras = {
  enabled?: boolean;
};

export type PriceAdjustmentExtras = {
  price_percentage?: number;
  price_lower_percentage?: number;
  price_upper_percentage?: number;
};

export type ContractExtras = ContactPreferenceExtras &
  ValuationAreaOverrideExtras &
  HideComponentsExtras &
  ColorThemeOverrideExtras &
  AdvertisementBannerOverrideExtras &
  TitleOverrideExtras &
  EstimationResultAnnotationExtras &
  ServiceAnnotationExtras &
  ConfirmDialogAnnotationExtras &
  EstimationResultContactOverrideExtras &
  GoogleTagManagerExtras &
  ReasonForSaleExtras &
  NewThemeEnabled &
  TrafficInfoAutoLinkExtras &
  ConnectingRoadInfoAutoExtras &
  PrivacyPolicyLabelTextExtras &
  ShopSettingsExtras &
  PriceAdjustmentExtras;

export type ContractFeaturesObject = {
  [feature: string]: ContractExtras;
};

export type Contract = {
  customerId: string | null;
  contractId: number | null;
  contractSearchId: string;
  companyName: string;
  displayName: string;
  contactMailAddress: string;
  contactUrl: string;
  contactAddress: string;
  contactTel: string;
  contactAgent: string;
  contactFax: string;
  displayAssessmentPriceAllFlag: boolean;
  advertiseTitle: string;
  advertiseLead: string;
  logoImage: string;
  logoLink: string;
  bannerImage: string;
  bannerLink: string;
  privacyPolicyText: string;
  privacyPolicyLink: string;
  cookieText: string;
  cookieLink: string;
  termsText: string;
  termsLink: string;
  copyright: string;
  features: string[];
  featuresObj: ContractFeaturesObject;
  prefectureInfo: PrefectureInfo[];
  propertyTypes: IPropertyType[];
  shopInfo: ShopInfo[];
  referralSource: ReferralSource[] | null;
};

const contract: Contract = {
  customerId: null,
  contractId: null,
  contractSearchId: "",
  companyName: "",
  displayName: "",
  contactMailAddress: "",
  contactUrl: "",
  contactAddress: "",
  contactTel: "",
  contactAgent: "",
  contactFax: "",
  displayAssessmentPriceAllFlag: false,
  advertiseTitle: "",
  advertiseLead: "",
  logoImage: "",
  logoLink: "",
  bannerImage: "",
  bannerLink: "",
  privacyPolicyText: "",
  privacyPolicyLink: "",
  cookieText: "",
  cookieLink: "",
  copyright: "",
  termsText: "",
  termsLink: "",
  features: [],
  featuresObj: {},
  prefectureInfo: [],
  propertyTypes: [],
  shopInfo: [],
  referralSource: [],
};

export const getSentence = (extras: ContactPreferenceExtras) => {
  if (extras && extras.sentence) {
    console.assert(typeof extras.sentence.negative !== "undefined", {
      extras,
      errorMsg: "extras.sentence.negative is not defined",
    });
    console.assert(typeof extras.sentence.positive !== "undefined", {
      extras,
      errorMsg: "extras.sentence.positive is not defined",
    });
    console.assert(typeof extras.sentence.use !== "undefined", {
      extras,
      errorMsg: "extras.sentence.use is not defined",
    });
    return extras.sentence[extras.sentence.use];
  }
  return null;
};

export const isControlFull = (control: ValuationAreaExtras | undefined): boolean => {
  return control === VALUATION_AREA_CONTROL.FULL;
};

export const isHiddenComponent = (
  features: ContractFeaturesObject,
  targetComponent: HiddenComponentSymbol
): boolean => {
  const extras = features[Feature.HIDE_COMPONENTS];
  if (!extras?.hidden_components) return false;
  return extras.hidden_components.includes(targetComponent);
};

export const isReasonForSaleComponentHidden = (features: ContractFeaturesObject): boolean => {
  const extras = features[Feature.REASON_FOR_SALE];
  if (!extras?.enabled) return false;
  return extras?.enabled;
};

export default contract;
